import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

const links = [
  { id: "struggles", label: "How We Help" },
  { id: "story", label: "Our Story" },
  { id: "book", label: "Book a Time" },
  { id: "contact", label: "Connect" },
];

const CashAppIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.4 3H6.6A3.6 3.6 0 0 0 3 6.6v10.8A3.6 3.6 0 0 0 6.6 21h10.8a3.6 3.6 0 0 0 3.6-3.6V6.6A3.6 3.6 0 0 0 17.4 3Zm-2.03 7.4a.6.6 0 0 1-.83.03 3.2 3.2 0 0 0-2.03-.83c-.62 0-1.27.24-1.27.78 0 .56.65.75 1.4.98 1.36.42 3.14 1 3.14 2.95 0 1.66-1.24 2.75-2.86 2.98l-.14.76a.6.6 0 0 1-.6.5h-.98a.6.6 0 0 1-.59-.7l.16-.87a4.8 4.8 0 0 1-2.35-1.06.6.6 0 0 1-.03-.86l.62-.6a.6.6 0 0 1 .82-.02c.61.52 1.42.85 2.28.85.83 0 1.4-.34 1.4-.9 0-.55-.6-.75-1.5-1.05-1.26-.4-2.98-.94-2.98-2.86 0-1.7 1.32-2.7 2.83-2.9l.15-.83a.6.6 0 0 1 .6-.5h.97a.6.6 0 0 1 .58.7l-.15.85c.65.19 1.25.5 1.77.94a.6.6 0 0 1 .03.86l-.6.6Z" />
  </svg>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FAF9F6]/70 border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0 })}
          className="text-left"
          data-testid="nav-logo"
        >
          <span className="font-serif-display text-2xl font-semibold tracking-tight text-[#2C2A28]">
            Wicks
          </span>
          <span className="hidden sm:inline font-serif-display text-2xl italic font-medium text-[#8A5A44] ml-1.5">
            Marriage Ministries
          </span>
        </button>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToSection(l.id)}
              className="text-sm text-[#5C5854] hover:text-[#8A5A44] transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 md:ml-8 lg:ml-14">
          <button
            onClick={() => scrollToSection("give")}
            className="relative rounded-full text-[#FFFDF6] text-sm px-6 py-2.5 bg-gradient-to-b from-[#F1943B] to-[#D97538] hover:from-[#F5A24E] hover:to-[#C86A2F] transition-colors duration-300 shadow-[0_0_0_0_rgba(241,148,59,0.6)] hover:shadow-[0_0_28px_2px_rgba(241,148,59,0.55)] ring-1 ring-[#F1943B]/60 nav-give-glow"
            data-testid="nav-donate-button"
          >
            <span className="relative z-10">Give</span>
          </button>
          <a
            href="https://cash.app/$mamawicks"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#00C244] hover:text-[#00A038] transition-colors duration-300 rounded-full border border-[#00C244]/40 hover:border-[#00C244] px-3 sm:px-3.5 py-2"
            data-testid="nav-cashapp-link"
            aria-label="Give via Cash App to $mamawicks"
          >
            <CashAppIcon size={13} />
            <span className="hidden sm:inline">$mamawicks</span>
            <span className="sm:hidden">Cash App</span>
          </a>
        </div>
      </div>
    </motion.header>
  );
};
